
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Library } from 'lucide-react';
import { ContentLibrary } from '../components/ContentLibrary';
import StorySelectionModal from '../components/StorySelectionModal';
import { useUserProfile } from '../hooks/useUserProfile';
import { usePageMeta } from '../hooks/usePageMeta';
import { CategoryItem } from '../types/category';
import { ContentSection } from '../types/content';
import { MembershipTier } from '../types/membership';
import { ChapterInfo } from '../utils/indexedDB';
import { CATEGORY_GROUPS } from '../data/categories';

interface StoriesPageProps {
  onStartReading: (
    content: string,
    sections?: ContentSection[],
    categoryId?: string,
    bookId?: string,
    bookTitle?: string,
    bookMetadata?: {
      totalWords?: number;
      author?: string;
      coverUrl?: string;
      chapters?: ChapterInfo[];
      sections?: ContentSection[];
      categoryId?: string;
    },
    initialWordIndex?: number,
    format?: string
  ) => void;
  userTier?: MembershipTier;
}

const StoriesPage: React.FC<StoriesPageProps> = ({ onStartReading, userTier }) => {
  const navigate = useNavigate();
  const [activeCategories, setActiveCategories] = useState<string[]>([]);

  // Category-specific meta descriptions
  const getCategoryDescription = (): string => {
    if (activeCategories.length === 0) {
      return 'Browse 500+ free reading stories across 25 genres — drama, horror, mystery, sci-fi, history, psychology, business, crime, comedy and more. Start reading online instantly with RSVP speed reading.';
    }
    if (activeCategories.length === 1) {
      const catName = activeCategories[0];
      const group = CATEGORY_GROUPS.find(g =>
        g.name.toLowerCase() === catName.toLowerCase()
      );
      if (group) {
        return `Read free ${catName.toLowerCase()} stories online. ${group.description} Browse our digital story library and start reading instantly with RSVP speed reading.`;
      }
      return `Read free ${catName.toLowerCase()} stories online. Browse our collection of ${catName.toLowerCase()} texts and start reading instantly with RSVP speed reading.`;
    }
    const catNames = activeCategories.map(c => c.toLowerCase()).join(', ');
    return `Read free stories online across ${activeCategories.length} categories: ${catNames}. Browse our digital story library and start reading instantly with RSVP speed reading.`;
  };

  usePageMeta({
    title: activeCategories.length === 1
      ? `${activeCategories[0]} Stories | Free Reading Stories Online | Flash-Read`
      : 'Free Reading Stories & Online Story Library | Flash-Read',
    description: getCategoryDescription(),
  });

  const { membershipProfile } = useUserProfile();
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState<{
    type: 'category';
    title: string;
    content: string;
    sections?: ContentSection[];
    categoryId?: string;
    difficulty: string;
    wordCount: number;
    readingTimeMinutes: number;
    source?: string;
    spoilerFreeSummary?: string;
    bookId?: string; // NEW: book identifier for progress tracking
  } | null>(null);

  const handleSelectCategoryItem = (item: CategoryItem) => {
    const sectionedItem = item as any;
    setModalItem({
      type: 'category',
      title: item.title,
      content: item.content,
      sections: sectionedItem.sections,
      categoryId: item.categoryId,
      difficulty: item.difficulty,
      wordCount: item.wordCount,
      readingTimeMinutes: item.readingTimeMinutes,
      source: item.source,
      spoilerFreeSummary: item.spoilerFreeSummary,
      bookId: item.id // Capture the stable book ID from CategoryItem
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalItem(null);
  };

  // Load content from section files
  const loadStoryContent = async (
    categoryId: string,
    sections?: ContentSection[]
  ): Promise<string> => {
    if (!sections || sections.length === 0) return '';
    
    const contentParts: string[] = [];
    
    for (const section of sections) {
      if (section.contentPath) {
        const fullPath = `/data/categories/${categoryId}/${section.contentPath}`;
        try {
          const response = await fetch(fullPath);
          if (response.ok) {
            const text = await response.text();
            contentParts.push(text);
          } else {
            console.error(`Failed to load section ${section.id}: ${response.status}`);
          }
        } catch (error) {
          console.error(`Error loading section ${section.id}:`, error);
        }
      }
    }
    
    return contentParts.join('\n\n');
  };

  const handleStartReadingFromModal = async () => {
    if (modalItem) {
      // Load content from section files
      const content = await loadStoryContent(
        modalItem.categoryId ?? '',
        modalItem.sections
      );
      
      // Build book metadata for progress tracking
      const bookMetadata = {
        totalWords: modalItem.wordCount,
        chapters: modalItem.sections?.map(s => ({
          chapterId: s.id,
          chapterTitle: s.title,
          wordCount: s.wordCount,
        })) as ChapterInfo[],
        categoryId: modalItem.categoryId,
        sections: modalItem.sections, // store section metadata for reload
      };
      onStartReading(
        content,          // ← Now contains actual content, not ''
        modalItem.sections,
        modalItem.categoryId,
        modalItem.bookId, // Pass bookId
        modalItem.title,  // Pass book title
        bookMetadata,     // Pass metadata
        undefined,       // initialWordIndex
        'txt'             // format: library stories are plain text
      );
      handleCloseModal();
    }
  };

  return (
    <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <div className="text-center flex-1">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">Free, Original</span> Stories
          </h1>

        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/library')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Library className="h-4 w-4" />
            Manage Library
          </button>
        </div>
      </div>

      {/* Keyword-rich intro paragraph */}
      <div className="stories-intro mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 max-w-3xl mx-auto">
        <p className="text-base md:text-lg text-slate-700 dark:text-slate-300 leading-relaxed text-center">
          Explore our <span className="keyword-highlight">online story library</span> with <span className="keyword-highlight">free reading stories</span>.<br />
          <span className="keyword-highlight">Read stories online</span> across 25 genres in our <span className="keyword-highlight">digital story library</span>.<br />
          <span className="keyword-highlight">Browse stories by category</span> and enjoy <span className="keyword-highlight">RSVP stories</span> at your pace.
        </p>
      </div>

      {/* Content Library */}
      <section>
        <ContentLibrary onSelectItem={handleSelectCategoryItem} onCategoryChange={setActiveCategories} />
      </section>

      {/* Story Selection Modal */}
      {modalItem && (
        <StorySelectionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onStartReading={handleStartReadingFromModal}
          title={modalItem.title}
          content={modalItem.content}
          wordCount={modalItem.wordCount}
          readingTimeMinutes={modalItem.readingTimeMinutes}
          difficulty={modalItem.difficulty}
          source={modalItem.source}
          spoilerFreeSummary={modalItem.spoilerFreeSummary}
          userTier={userTier}
        />
      )}

      {/* CTA: Unlock Unlimited Stories (guest/free tier only) */}
      {(!userTier || userTier === 'guest') && (
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Want unlimited access to all stories?</h3>
           <p className="text-blue-100 mb-6">Sign up to unlock 500+ stories across 25 genres, save your progress, and track your reading stats.</p>
            <button
              onClick={() => navigate('/signup')}
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 font-bold rounded-full hover:bg-blue-50 transition-all shadow-lg"
            >
              Sign Up →
            </button>
        </div>
      )}
    </div>
  );
};

export default StoriesPage;
